import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { NavBar } from '../components/NavBar';
import { Calendar, Zap, ChevronRight, PlayCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

import { StatsGrid } from '../components/dashboard/StatsGrid';
import { QuickLinksWheel } from '../components/dashboard/QuickLinksWheel';
import { RecentActivity } from '../components/dashboard/RecentActivity';
import { CreatePostBox } from '../components/dashboard/CreatePostBox';
import { WorkoutCalendarModal } from '../components/dashboard/WorkoutCalendarModal';

export const DashboardPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekly, setWeekly] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Create Post state
  const [postContent, setPostContent] = useState('');
  const [postType, setPostType] = useState('general');
  const [posting, setPosting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Available post types based on user activity
  const [hasRecentWorkout, setHasRecentWorkout] = useState(false);
  const [hasAchievement, setHasAchievement] = useState(false);
  const [hasMilestone, setHasMilestone] = useState(false);
  const [recentWorkoutTitle, setRecentWorkoutTitle] = useState('');

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      navigate('/onboarding');
      return;
    }

    const fetchData = async () => {
      try {
        const [statsRes, logsRes, weeklyRes, achRes] = await Promise.all([
          api().get('/stats'),
          api().get('/logs'),
          api().get('/logs/weekly-summary'),
          // Calling /achievements here auto-awards any newly-met milestones
          // server-side, so the user sees the celebration on the home page
          // the moment they log in — no need to visit /badges first.
          api().get('/achievements').catch(() => ({ data: { newly_unlocked: [] } })),
        ]);
        setStats(statsRes.data);
        setWeekly(weeklyRes.data);
        const logs = logsRes.data || [];
        setRecentWorkouts(logs.slice(0, 5));

        // 🎉 Smaller dashboard celebration — single confetti burst from the
        // bottom center, plus a clickable toast that nudges users over to
        // the trophy case to see all their badges.
        const fresh = achRes.data?.newly_unlocked || [];
        if (fresh.length > 0) {
          confetti({
            particleCount: 60,
            spread: 80,
            startVelocity: 38,
            origin: { x: 0.5, y: 0.85 },
            colors: ['#FFD700', '#FFA500', '#FFFFFF', '#38BDF8'],
          });
          fresh.forEach((ach, i) => {
            setTimeout(() => {
              toast.success(`Achievement Unlocked: ${ach.name}!`, {
                description: ach.description,
                duration: 6000,
                action: {
                  label: 'View',
                  onClick: () => navigate('/badges'),
                },
              });
            }, i * 800);
          });
        }

        const now = new Date();
        const recentLog = logs.find((log) => {
          const hoursDiff = (now - new Date(log.completed_at)) / (1000 * 60 * 60);
          return hoursDiff <= 24;
        });
        if (recentLog) {
          setHasRecentWorkout(true);
          setRecentWorkoutTitle(recentLog.workout_title);
        }

        const currentStreak = statsRes.data?.current_streak || 0;
        const totalWorkouts = statsRes.data?.total_workouts || 0;
        if ([1, 10, 50, 100].includes(totalWorkouts) || [7, 30].includes(currentStreak)) {
          setHasAchievement(true);
        }
        if (user?.target_weight && user?.weight) {
          const startWeight = user.starting_weight || user.weight;
          const progress = Math.abs(user.weight - startWeight);
          const goal = Math.abs(user.target_weight - startWeight);
          if (goal > 0 && progress >= goal * 0.25) setHasMilestone(true);
        }
        if (currentStreak >= 7) setHasMilestone(true);
      } catch (e) {
        console.debug('Error:', e?.message);
      }
      setLoading(false);
    };
    fetchData();
  }, [api, user, navigate]);

  const weeklyGoal = weekly?.weekly_goal || 5;
  const weeklyCount = weekly?.workout_count || 0;
  const weekProgress = Math.min((weeklyCount / weeklyGoal) * 100, 100);

  const handleImageSelect = (file) => {
    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
  };

  const handleCreatePost = async () => {
    if (!postContent.trim() && !selectedImage) {
      toast.error('Please enter some content or add an image');
      return;
    }
    setPosting(true);
    try {
      let imageUrl = null;
      if (selectedImage) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', selectedImage);
        const uploadRes = await api().post('/social/posts/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        imageUrl = uploadRes.data.image_url;
        setUploadingImage(false);
      }
      await api().post('/social/posts', { content: postContent, post_type: postType, image_url: imageUrl });
      toast.success('Post created!');
      setPostContent('');
      setPostType('general');
      handleRemoveImage();
    } catch (err) {
      toast.error('Failed to create post');
    }
    setPosting(false);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="dashboard-page">
      <NavBar title="Dashboard" />

      <main className="max-w-2xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mb-6 text-center"
        >
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider">
            Welcome Back, <span className="text-primary">{user?.name?.split(' ')[0] || 'Athlete'}</span>
          </h2>
        </motion.div>

        {/* Tutorial trigger — 99s product walkthrough (opens dedicated page) */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.02, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mb-6 flex justify-center"
          data-testid="dashboard-tutorial-section"
        >
          <button
            onClick={() => navigate('/walkthrough')}
            className="group inline-flex items-center gap-2 rounded-full border border-primary/30 hover:border-primary/60 bg-primary/[0.06] hover:bg-primary/[0.12] transition-all px-3.5 py-1.5 shadow-[0_2px_10px_rgba(250,204,21,0.08)] hover:shadow-[0_4px_16px_rgba(250,204,21,0.18)]"
            data-testid="dashboard-tutorial-button"
          >
            <span className="h-6 w-6 rounded-full bg-primary text-black flex items-center justify-center flex-shrink-0">
              <PlayCircle className="h-3.5 w-3.5" strokeWidth={2} />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-white">
              99-Second <span className="text-primary">Tutorial</span>
            </span>
            <ChevronRight className="h-3.5 w-3.5 text-primary/70 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
          </button>
        </motion.section>

        {/* Motivational tagline — pulled below the tutorial so users land on
            the app tour first, then get the "let's go" push right before the
            action wheel. */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.05, duration: 0.5 }}
          className="text-muted-foreground text-lg text-center mb-8"
        >
          Ready to surpass your goals today?
        </motion.p>

        <QuickLinksWheel />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.04, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <CreatePostBox
            user={user}
            postContent={postContent}
            setPostContent={setPostContent}
            postType={postType}
            setPostType={setPostType}
            posting={posting}
            uploadingImage={uploadingImage}
            hasRecentWorkout={hasRecentWorkout}
            hasAchievement={hasAchievement}
            hasMilestone={hasMilestone}
            recentWorkoutTitle={recentWorkoutTitle}
            selectedImage={selectedImage}
            imagePreview={imagePreview}
            onImageSelect={handleImageSelect}
            onRemoveImage={handleRemoveImage}
            onCreatePost={handleCreatePost}
          />
        </motion.div>

        <StatsGrid stats={stats} />

        <div className="grid lg:grid-cols-3 gap-3 mb-6">
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={() => setCalendarOpen(true)}
            className="lg:col-span-2 bg-card border border-border rounded-2xl p-4 text-left hover:border-primary/50 transition-colors cursor-pointer card-hover"
            data-testid="weekly-goal-card"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-heading text-lg text-white uppercase tracking-wider leading-tight">Weekly Goal</h3>
                <p className="text-muted-foreground text-xs mt-0.5">{weeklyCount} of {weeklyGoal} workouts</p>
              </div>
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <Progress value={weekProgress} className="h-2.5 bg-black" />
            <div className="flex justify-between mt-1.5 text-[11px] text-muted-foreground">
              <span>0</span>
              <span>{weeklyGoal} workouts</span>
            </div>
            <div className="mt-2 text-[10px] text-primary uppercase tracking-wider">Tap to view workout calendar →</div>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="bg-primary rounded-2xl p-4 flex flex-col justify-between card-hover"
          >
            <div>
              <Zap className="h-6 w-6 text-primary-foreground mb-2" />
              <h3 className="font-heading text-lg text-primary-foreground uppercase tracking-wider leading-tight">Start Workout</h3>
              <p className="text-primary-foreground/80 text-xs mt-0.5">Jump into your next session</p>
            </div>
            <Link to="/workouts">
              <Button
                variant="secondary"
                className="w-full mt-3 h-9 text-xs bg-black text-white font-heading tracking-wider uppercase hover:bg-black/80"
                data-testid="start-workout-btn"
              >
                Browse Workouts
                <ChevronRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>

        <RecentActivity recentWorkouts={recentWorkouts} />
      </main>

      <WorkoutCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        api={api}
      />
    </div>
  );
};
